import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'

import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'
import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission } from '../../utilities/helpers'
import {
  getFnbLocations,
  getFnbFulfillmentQueue,
  getOrgVenues,
  transitionFnbOrder,
} from '../../utilities/api'

import { NoPermissionsContainer } from '../NoPermissionsContainer'

import './fulfillmentBoard.scss'

const VENUE_STORAGE_KEY = 'fnb-fulfillment-venue'
const LOCATION_STORAGE_KEY = 'fnb-fulfillment-location'
const ALL_LOCATIONS_VALUE = 'all'
const POLL_INTERVAL_MS = 5000

function getRowAttrs(row) {
  return row.attributes ?? row
}

const COLUMNS = [
  { key: 'received', title: 'New', statuses: ['received'] },
  {
    key: 'in_progress',
    title: 'In progress',
    statuses: ['accepted', 'preparing'],
  },
  { key: 'ready', title: 'Ready', statuses: ['ready'] },
]

const NEXT_ACTION = {
  received: { to: 'accepted', label: 'Accept' },
  accepted: { to: 'preparing', label: 'Start' },
  preparing: { to: 'ready', label: 'Mark ready' },
  ready: { to: 'delivered', label: 'Complete & deliver', needsConfirm: true },
}

function formatCurrency(value) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value ?? 0))
}

function formatSeat(seat) {
  return [seat?.section, seat?.row, seat?.seat].filter(Boolean).join(' · ')
}

function hasMarketplacePermission(getPermissions, orgPermissionsReady) {
  if (!orgPermissionsReady) return false
  const perms = getPermissions()
  return (
    checkPermission(perms, ORGANIZATION_PERMISSION_SLUGS.MANAGE_MARKETPLACE) ||
    checkPermission(perms, ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT)
  )
}

function playNewOrderSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.value = 880
    gain.gain.value = 0.08
    oscillator.connect(gain)
    gain.connect(ctx.destination)
    oscillator.start()
    setTimeout(() => {
      oscillator.stop()
      ctx.close()
    }, 180)
  } catch {
    // Audio not available
  }
}

export default function FulfillmentBoardWrapper({ fullscreen = false }) {
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgUUID = AuthService.getOrg()[0]?.uuid
  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission = hasMarketplacePermission(
    getPermissions,
    orgPermissionsReady
  )

  const [venues, setVenues] = useState([])
  const [venueId, setVenueId] = useState(
    () => localStorage.getItem(VENUE_STORAGE_KEY) || ''
  )
  const [locations, setLocations] = useState([])
  const [locationId, setLocationId] = useState(
    () => localStorage.getItem(LOCATION_STORAGE_KEY) || ''
  )
  const [orders, setOrders] = useState([])
  const [counts, setCounts] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [actingOrderId, setActingOrderId] = useState(null)
  const [completeOrder, setCompleteOrder] = useState(null)
  const [activeColumn, setActiveColumn] = useState('received')

  const previousNewCountRef = useRef(null)

  const requiresVenueSelection = venues.length > 1
  const defaultVenueUuid = venues.length === 1 ? (venues[0]?.uuid ?? '') : ''
  const effectiveVenueUuid = venueId || defaultVenueUuid

  const visibleLocations = useMemo(() => {
    if (!effectiveVenueUuid) return locations

    return locations.filter((row) => {
      const attrs = getRowAttrs(row)
      if (!attrs.venue_uuid) return true
      return attrs.venue_uuid === effectiveVenueUuid
    })
  }, [locations, effectiveVenueUuid])

  const loadVenues = useCallback(async () => {
    if (!orgUUID) return
    try {
      const res = await getOrgVenues(orgUUID)
      const rows = Array.isArray(res?.data) ? res.data : []
      setVenues(rows)
    } catch (err) {
      console.error(err)
    }
  }, [orgUUID])

  const loadLocations = useCallback(async () => {
    if (!orgUUID) return
    try {
      const res = await getFnbLocations(orgUUID)
      const rows = res.data?.data ?? []
      setLocations(rows)
    } catch (err) {
      console.error(err)
    }
  }, [orgUUID])

  useEffect(() => {
    if (venues.length !== 1 || venueId) return

    const uuid = venues[0]?.uuid
    if (!uuid) return

    setVenueId(uuid)
    localStorage.setItem(VENUE_STORAGE_KEY, uuid)
  }, [venues, venueId])

  useEffect(() => {
    if (requiresVenueSelection && !effectiveVenueUuid) return

    if (
      locationId &&
      locationId !== ALL_LOCATIONS_VALUE &&
      !visibleLocations.some((row) => String(row.id) === locationId)
    ) {
      setLocationId('')
      localStorage.removeItem(LOCATION_STORAGE_KEY)
      return
    }

    if (locationId) return

    if (visibleLocations.length === 1) {
      const id = String(visibleLocations[0].id)
      setLocationId(id)
      localStorage.setItem(LOCATION_STORAGE_KEY, id)
    } else if (
      visibleLocations.length > 1 &&
      !localStorage.getItem(LOCATION_STORAGE_KEY)
    ) {
      setLocationId(ALL_LOCATIONS_VALUE)
      localStorage.setItem(LOCATION_STORAGE_KEY, ALL_LOCATIONS_VALUE)
    }
  }, [requiresVenueSelection, effectiveVenueUuid, visibleLocations, locationId])

  const loadQueue = useCallback(async () => {
    if (!orgUUID || !locationId) {
      setIsLoading(false)
      return
    }

    if (requiresVenueSelection && !effectiveVenueUuid) {
      setIsLoading(false)
      return
    }

    try {
      const res = await getFnbFulfillmentQueue({
        organizationUuid: orgUUID,
        ...(effectiveVenueUuid ? { venueUuid: effectiveVenueUuid } : {}),
        ...(locationId !== ALL_LOCATIONS_VALUE ? { locationId } : {}),
      })
      const data = res.data ?? {}
      const nextOrders = data.orders ?? []
      const nextCounts = data.counts ?? {}

      const newCount = Number(nextCounts.received ?? 0)
      if (
        previousNewCountRef.current !== null &&
        newCount > previousNewCountRef.current
      ) {
        playNewOrderSound()
        setActiveColumn('received')
      }
      previousNewCountRef.current = newCount

      setOrders(nextOrders)
      setCounts(nextCounts)
      setError('')
    } catch (err) {
      console.error(err)
      setError('Unable to load orders. Retrying…')
    } finally {
      setIsLoading(false)
    }
  }, [orgUUID, locationId, requiresVenueSelection, effectiveVenueUuid])

  useEffect(() => {
    loadVenues()
    loadLocations()
  }, [loadVenues, loadLocations])

  useEffect(() => {
    loadQueue()
    const timer = setInterval(loadQueue, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [loadQueue])

  const handleVenueChange = (event) => {
    const nextVenueId = event.target.value
    setVenueId(nextVenueId)
    localStorage.setItem(VENUE_STORAGE_KEY, nextVenueId)
    setLocationId('')
    localStorage.removeItem(LOCATION_STORAGE_KEY)
    setIsLoading(true)
    previousNewCountRef.current = null
  }

  const handleLocationChange = (event) => {
    const nextId = event.target.value
    setLocationId(nextId)
    localStorage.setItem(LOCATION_STORAGE_KEY, nextId)
    setIsLoading(true)
    previousNewCountRef.current = null
  }

  const handleTransition = async (order, to) => {
    const action = NEXT_ACTION[order.fulfillmentStatus]
    if (action?.needsConfirm && to === 'delivered') {
      setCompleteOrder(order)
      return
    }

    setActingOrderId(order.id)
    try {
      await transitionFnbOrder(order.id, {
        to,
        ...(locationId && locationId !== ALL_LOCATIONS_VALUE
          ? { locationId }
          : {}),
      })
      await loadQueue()
    } catch (err) {
      console.error(err)
      setError('Could not update order status.')
    } finally {
      setActingOrderId(null)
    }
  }

  const handleCancel = async (order) => {
    const reason = window.prompt('Cancellation reason (optional):') ?? ''
    setActingOrderId(order.id)
    try {
      await transitionFnbOrder(order.id, {
        to: 'cancelled',
        cancelReason: reason || null,
      })
      await loadQueue()
    } catch (err) {
      console.error(err)
      setError('Could not cancel order.')
    } finally {
      setActingOrderId(null)
    }
  }

  const handleCompleteDelivery = async () => {
    if (!completeOrder) return
    setActingOrderId(completeOrder.id)
    try {
      await transitionFnbOrder(completeOrder.id, {
        to: 'delivered',
        ...(locationId && locationId !== ALL_LOCATIONS_VALUE
          ? { locationId }
          : {}),
      })
      setCompleteOrder(null)
      await loadQueue()
    } catch (err) {
      console.error(err)
      setError('Could not complete delivery.')
    } finally {
      setActingOrderId(null)
    }
  }

  const ordersByColumn = useMemo(() => {
    const grouped = Object.fromEntries(COLUMNS.map((col) => [col.key, []]))
    for (const order of orders) {
      const column = COLUMNS.find((col) =>
        col.statuses.includes(order.fulfillmentStatus)
      )
      if (column) grouped[column.key].push(order)
    }
    return grouped
  }, [orders])

  const queueReady =
    Boolean(locationId) &&
    (!requiresVenueSelection || Boolean(effectiveVenueUuid))

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  return (
    <div
      className={`fulfillment-board${fullscreen ? ' fulfillment-board--fullscreen' : ''}`}
    >
      <div className="fulfillment-board__sticky-bar">
        <header className="fulfillment-board__header">
          <div>
            <h1 className="fulfillment-board__title">Live orders</h1>
            <p className="fulfillment-board__subtitle">
              Manage incoming food &amp; beverage orders
              {requiresVenueSelection
                ? ' for your venue and location'
                : ' for your location'}
            </p>
          </div>
          <div className="fulfillment-board__controls">
            {requiresVenueSelection && (
              <Form.Select
                value={venueId}
                onChange={handleVenueChange}
                aria-label="Fulfillment venue"
                className="fulfillment-board__venue-select"
              >
                <option value="">Select venue…</option>
                {venues.map((venue) => (
                  <option key={venue.uuid} value={venue.uuid}>
                    {venue.name ?? venue.uuid}
                  </option>
                ))}
              </Form.Select>
            )}
            <Form.Select
              value={locationId}
              onChange={handleLocationChange}
              aria-label="Fulfillment location"
              className="fulfillment-board__location-select"
              disabled={requiresVenueSelection && !venueId}
            >
              <option value="">
                {requiresVenueSelection && !venueId
                  ? 'Select venue first…'
                  : 'Select location…'}
              </option>
              {visibleLocations.length > 1 && (
                <option value={ALL_LOCATIONS_VALUE}>All locations</option>
              )}
              {visibleLocations.map((row) => {
                const attrs = getRowAttrs(row)
                return (
                  <option key={row.id} value={String(row.id)}>
                    {attrs.name}
                  </option>
                )
              })}
            </Form.Select>
            <span className="fulfillment-board__badge">
              {counts.received ?? 0} new
            </span>
          </div>
        </header>

        {queueReady && (
          <nav
            className="fulfillment-board__tabs"
            aria-label="Order queue sections"
          >
            {COLUMNS.map((column) => {
              const count = ordersByColumn[column.key].length
              const isActive = activeColumn === column.key
              return (
                <button
                  key={column.key}
                  type="button"
                  className={`fulfillment-board__tab${isActive ? ' is-active' : ''}${
                    column.key === 'received' && count > 0 ? ' has-alert' : ''
                  }`}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => setActiveColumn(column.key)}
                >
                  <span className="fulfillment-board__tab-label">
                    {column.title}
                  </span>
                  <span className="fulfillment-board__tab-count">{count}</span>
                </button>
              )
            })}
          </nav>
        )}
      </div>

      {error && <p className="fulfillment-board__error">{error}</p>}

      {!queueReady ? (
        <div className="fulfillment-board__empty">
          <p>
            {requiresVenueSelection && !effectiveVenueUuid
              ? 'Select a venue to view its order queue.'
              : 'Select a location to view its order queue.'}
          </p>
        </div>
      ) : isLoading && orders.length === 0 ? (
        <div className="fulfillment-board__empty">
          <p>Loading orders…</p>
        </div>
      ) : (
        <div className="fulfillment-board__columns">
          {COLUMNS.map((column) => (
            <div
              key={column.key}
              className={`fulfillment-board__column${
                activeColumn === column.key
                  ? ' fulfillment-board__column--active'
                  : ''
              }`}
            >
              <header className="fulfillment-board__column-header">
                <h2>{column.title}</h2>
                <span>{ordersByColumn[column.key].length}</span>
              </header>
              <div className="fulfillment-board__cards">
                {ordersByColumn[column.key].length === 0 ? (
                  <p className="fulfillment-board__column-empty">No orders</p>
                ) : (
                  ordersByColumn[column.key].map((order) => {
                    const nextAction = NEXT_ACTION[order.fulfillmentStatus]
                    const isActing = actingOrderId === order.id
                    return (
                      <article
                        key={order.id}
                        className="fulfillment-board__card"
                      >
                        <div className="fulfillment-board__card-top">
                          <strong className="fulfillment-board__seat">
                            {formatSeat(order.seat) || 'No seat'}
                          </strong>
                          <span className="fulfillment-board__age">
                            {order.ageMinutes}m
                          </span>
                        </div>
                        <p className="fulfillment-board__order-number">
                          {order.orderNumber}
                          {locationId === ALL_LOCATIONS_VALUE &&
                            order.locationName && (
                              <span className="fulfillment-board__location-tag">
                                {' '}
                                · {order.locationName}
                              </span>
                            )}
                        </p>
                        <ul className="fulfillment-board__items">
                          {order.items.map((item) => (
                            <li key={item.id}>
                              {item.quantity}× {item.itemName}
                            </li>
                          ))}
                        </ul>
                        {order.notes && (
                          <p className="fulfillment-board__notes">
                            Note: {order.notes}
                          </p>
                        )}
                        <div className="fulfillment-board__card-footer">
                          <span>{formatCurrency(order.total)}</span>
                          <span className="fulfillment-board__payment">
                            {order.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                          </span>
                        </div>
                        <div className="fulfillment-board__actions">
                          {nextAction && (
                            <Button
                              size="lg"
                              variant="primary"
                              className="fulfillment-board__action-btn"
                              disabled={isActing}
                              onClick={() =>
                                handleTransition(order, nextAction.to)
                              }
                            >
                              {isActing ? '…' : nextAction.label}
                            </Button>
                          )}
                          {order.fulfillmentStatus !== 'cancelled' &&
                            order.fulfillmentStatus !== 'delivered' && (
                              <Button
                                size="lg"
                                variant="outline-danger"
                                className="fulfillment-board__action-btn fulfillment-board__action-btn--secondary"
                                disabled={isActing}
                                onClick={() => handleCancel(order)}
                              >
                                Cancel
                              </Button>
                            )}
                        </div>
                      </article>
                    )
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        show={Boolean(completeOrder)}
        onHide={() => !actingOrderId && setCompleteOrder(null)}
        centered
        fullscreen="sm-down"
        className="fulfillment-board__payment-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Complete & deliver</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {completeOrder && (
            <>
              <p>
                <strong>{completeOrder.orderNumber}</strong> —{' '}
                {formatSeat(completeOrder.seat)}
              </p>
              <p className="mb-0">
                Total: <strong>{formatCurrency(completeOrder.total)}</strong>
                {' · '}
                Paid at checkout
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="fulfillment-board__payment-footer">
          <Button
            variant="secondary"
            size="lg"
            className="fulfillment-board__action-btn"
            disabled={Boolean(actingOrderId)}
            onClick={() => setCompleteOrder(null)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="lg"
            className="fulfillment-board__action-btn"
            disabled={Boolean(actingOrderId)}
            onClick={handleCompleteDelivery}
          >
            {actingOrderId ? 'Processing…' : 'Complete & deliver'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
